import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const ASSETS = {
  logo: "/blink-logo-transparent.svg",
  danSteinway: "https://customer-assets.emergentagent.com/job_affectionate-bhabha-5/artifacts/idzjqp05_IMG_3631.jpeg",
  gianniWindow: "https://customer-assets.emergentagent.com/job_affectionate-bhabha-5/artifacts/4zjrzfln_image.png",
};

const SPOTIFY = {
  playlist: "https://open.spotify.com/playlist/3EaOgBW6yQ7Ev2oXNBLtwO",
  playlistEmbed: "3EaOgBW6yQ7Ev2oXNBLtwO",
  dan: "https://open.spotify.com/artist/7B9v56y6PJC3fEyNTpNw9p",
  gianni: "https://open.spotify.com/artist/2QKA2e6Y84WiIXySuMNAnZ",
  danEmbed: "7B9v56y6PJC3fEyNTpNw9p",
  gianniEmbed: "2QKA2e6Y84WiIXySuMNAnZ",
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, y: 0, 
    transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] } 
  }
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

export default function HomePage() {
  useEffect(() => {
    document.title = "Blink Records | Human Touch. Human Made Music.";
  }, []);

  return (
    <div className="bg-[#F5F3EF] min-h-screen" data-testid="blink-records-landing">
      
      {/* SECTION 1 — HERO */}
      <section className="hero-minimal" data-testid="hero-section">
        <nav className="hero-nav" data-testid="hero-nav">
          <a href="https://www.instagram.com/blinkrecordsllc/?hl=en" target="_blank" rel="noopener noreferrer" className="nav-icon-link nav-icon-instagram" aria-label="Instagram">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
          </a>
          <a href="https://www.youtube.com/channel/UC22EnDYf_JqPcav6mzLHgZg" target="_blank" rel="noopener noreferrer" className="nav-icon-link nav-icon-youtube" aria-label="YouTube">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
          </a>
          <a href="https://music.apple.com/us/artist/gianni-staiano/1785805874" target="_blank" rel="noopener noreferrer" className="nav-icon-link nav-icon-apple" aria-label="Apple Music">
            <img src="/icons/apple-music.png" alt="Apple Music" className="nav-icon-img" />
          </a>
          <Link to="/login" className="nav-icon-link nav-login-icon" data-testid="nav-login-link" aria-label="Login">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </Link>
        </nav>

        <div className="hero-radial-burst" />
        
        <motion.div className="hero-content-minimal" initial="hidden" animate="visible" variants={stagger}>
          <motion.div variants={fadeUp} className="mb-4 logo-wrapper">
            <img src={ASSETS.logo} alt="Blink Records" className="w-56 md:w-72 lg:w-80 mx-auto logo-blend" data-testid="logo" />
          </motion.div>

          <motion.p variants={fadeUp} className="font-sans text-base md:text-lg lg:text-xl tracking-[0.25em] text-[#4A4744] mb-16">
            blink records
          </motion.p>

          <motion.h1 variants={fadeUp} className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-wide text-[#1A1918] mb-14">
            Human Touch. Human Made Music.
          </motion.h1>

          <motion.div variants={fadeUp}>
            <a href={SPOTIFY.playlist} target="_blank" rel="noopener noreferrer" className="cta-minimal" data-testid="hero-cta">
              Listen Now
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* SECTION 2 — NEW RELEASES */}
      <section className="section-padding bg-[#FAF9F7]" data-testid="new-releases-section">
        <motion.div className="max-w-5xl mx-auto" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
          <motion.p variants={fadeUp} className="font-sans text-xs uppercase tracking-[0.2em] text-[#A8A5A0] mb-10 text-center">New Releases</motion.p>
          <motion.div variants={fadeUp} className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* Track 1 — Patience by Dan Leitner */}
            <div data-testid="new-release-1">
              <a href="https://open.spotify.com/album/6huK0fDDtbsVlDxSluH2AI" target="_blank" rel="noopener noreferrer" className="block release-artwork-link">
                <img src="/artwork/patience.webp" alt="Patience - Dan Leitner" className="release-artwork" />
              </a>
              <div className="release-player">
                <iframe
                  src="https://open.spotify.com/embed/album/6huK0fDDtbsVlDxSluH2AI?utm_source=generator&theme=0"
                  width="100%" height="80" frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy" title="Patience - Dan Leitner" style={{ borderRadius: '8px' }}
                />
              </div>
            </div>

            {/* Track 2 — Night Light by Gianni Staiano */}
            <div data-testid="new-release-2">
              <a href="https://open.spotify.com/album/1Bz5Gd6w37gFOkBAeiqFKI" target="_blank" rel="noopener noreferrer" className="block release-artwork-link">
                <img src="/artwork/nightlight.webp" alt="Night Light - Gianni Staiano" className="release-artwork" />
              </a>
              <div className="release-player">
                <iframe
                  src="https://open.spotify.com/embed/album/1Bz5Gd6w37gFOkBAeiqFKI?utm_source=generator&theme=0"
                  width="100%" height="80" frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy" title="Night Light - Gianni Staiano" style={{ borderRadius: '8px' }}
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* SECTION 3 — PLAYLIST */}
      <section className="section-padding" data-testid="playlist-section">
        <motion.div className="max-w-3xl mx-auto" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
          <motion.p variants={fadeUp} className="font-sans text-xs uppercase tracking-[0.2em] text-[#A8A5A0] mb-8 text-center">Listen</motion.p>
          <motion.div variants={fadeUp} className="spotify-container-large">
            <iframe
              src={`https://open.spotify.com/embed/playlist/${SPOTIFY.playlistEmbed}?utm_source=generator&theme=0&autoplay=1`}
              width="100%" height="450" frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="eager" title="Blink Records Playlist" style={{ borderRadius: '12px' }}
            />
          </motion.div>
        </motion.div>
      </section>

      {/* SECTION — MASSAGE & SLEEP */}
      <section className="section-padding bg-[#FAF9F7]" data-testid="massage-sleep-section">
        <motion.div className="max-w-4xl mx-auto" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
          <motion.p variants={fadeUp} className="font-sans text-xs uppercase tracking-[0.2em] text-[#A8A5A0] mb-10 text-center">Massage &amp; Sleep</motion.p>
          <motion.div variants={fadeUp} className="massage-sleep-card">
            <a href="https://open.spotify.com/playlist/1jYq1FXIaCTOHvJjJ68Lff" target="_blank" rel="noopener noreferrer" className="block release-artwork-link">
              <img src="/artwork/madrugada.jpg" alt="Blink Records Massage" className="release-artwork" />
            </a>
            <div className="release-player">
              <iframe
                src="https://open.spotify.com/embed/playlist/1jYq1FXIaCTOHvJjJ68Lff?utm_source=generator&theme=0"
                width="100%" height="352" frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy" title="Blink Records Massage" style={{ borderRadius: '12px' }}
              />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* SECTION — TWO ARTISTS */}
      <section className="section-padding" data-testid="artists-section">
        <motion.div className="max-w-6xl mx-auto" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-16">
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-light text-[#1A1918] mb-4">Two artists. One system.</h2>
            <p className="font-serif text-base md:text-lg font-light italic text-[#6B6966]">Music made through instinct, not algorithm.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 md:gap-16">
            <motion.div variants={fadeUp} className="artist-card">
              <a href={SPOTIFY.dan} target="_blank" rel="noopener noreferrer" className="block mb-6 overflow-hidden rounded-sm">
                <img src={ASSETS.danSteinway} alt="Dan Leitner" className="artist-image-warm" data-testid="dan-image" />
              </a>
              <h3 className="font-serif text-xl md:text-2xl font-light text-[#1A1918] mb-4 text-center">Dan Leitner</h3>
              <div className="spotify-container p-3">
                <iframe src={`https://open.spotify.com/embed/artist/${SPOTIFY.danEmbed}?utm_source=generator&theme=0`} width="100%" height="152" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" title="Dan Leitner" style={{ borderRadius: '12px' }} />
              </div>
              <a href="https://youtube.com/@danleitner1229" target="_blank" rel="noopener noreferrer" className="artist-youtube-link" data-testid="dan-youtube-link">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                YouTube
              </a>
            </motion.div>

            <motion.div variants={fadeUp} className="artist-card">
              <a href={SPOTIFY.gianni} target="_blank" rel="noopener noreferrer" className="block mb-6 overflow-hidden rounded-sm">
                <img src={ASSETS.gianniWindow} alt="Gianni Staiano" className="artist-image-bw" data-testid="gianni-image" />
              </a>
              <h3 className="font-serif text-xl md:text-2xl font-light text-[#1A1918] mb-4 text-center">Gianni Staiano</h3>
              <div className="spotify-container p-3">
                <iframe src={`https://open.spotify.com/embed/artist/${SPOTIFY.gianniEmbed}?utm_source=generator&theme=0`} width="100%" height="152" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" title="Gianni Staiano" style={{ borderRadius: '12px' }} />
              </div>
              <a href="https://youtube.com/@giannistaianomusic" target="_blank" rel="noopener noreferrer" className="artist-youtube-link" data-testid="gianni-youtube-link">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                YouTube
              </a>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* SECTION — VISUAL BREAK (Gianni Hands Video) */}
      <div className="visual-break-video" data-testid="visual-break">
        <div className="video-container">
          <video
            src="/videos/gianni-hands.mp4"
            autoPlay
            muted
            loop
            playsInline
            className="video-native"
          />
        </div>
        <div className="video-overlay">
          <motion.p className="visual-break-text" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.3 }}>
            Felt before it's understood.
          </motion.p>
        </div>
      </div>

      {/* SECTION 5 — BRAND STATEMENT */}
      <section className="section-padding bg-[#FAF9F7]" data-testid="brand-statement">
        <motion.div className="max-w-2xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
          <motion.p variants={fadeUp} className="font-serif text-xl md:text-2xl lg:text-3xl font-light text-[#1A1918] leading-relaxed mb-6">Music made by human instinct.</motion.p>
          <motion.p variants={fadeUp} className="font-serif text-lg md:text-xl font-light text-[#6B6966] leading-relaxed mb-6">Composed in moments that can't be replicated.</motion.p>
          <motion.p variants={fadeUp} className="font-serif text-lg md:text-xl font-light italic text-[#A8A5A0] leading-relaxed">Built to be felt, not analyzed.</motion.p>
        </motion.div>
      </section>

      {/* SECTION 6 — FINAL CTA */}
      <section className="section-padding" data-testid="final-cta">
        <motion.div className="max-w-xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
          <motion.div variants={fadeUp}>
            <a href={SPOTIFY.playlist} target="_blank" rel="noopener noreferrer" className="cta-primary" data-testid="final-cta-button">Listen Now</a>
          </motion.div>
        </motion.div>
      </section>

      {/* SECTION 7 — FOOTER */}
      <footer className="footer" data-testid="footer">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <p className="font-serif text-xl text-[#FAF9F7] tracking-wide">blink records</p>
            </div>
            <div className="flex items-center gap-8">
              <a href={SPOTIFY.dan} target="_blank" rel="noopener noreferrer" className="font-sans text-xs uppercase tracking-[0.15em]">Dan Leitner</a>
              <a href={SPOTIFY.gianni} target="_blank" rel="noopener noreferrer" className="font-sans text-xs uppercase tracking-[0.15em]">Gianni Staiano</a>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-[#2A2928] text-center">
            <p className="font-sans text-xs text-[#6B6966]">&copy; {new Date().getFullYear()} blink records</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
